import React, { useState } from 'react';
import {
  CheckCircle2,
  XCircle,
  RefreshCw,
  Clock,
  Eye,
  AlertTriangle,
  Activity,
  Users,
  ArrowLeftRight,
  MousePointer,
  ChevronDown,
  ChevronUp,
  Lightbulb,
  Search,
  Scale,
  Shield,
  Lock,
  List
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────
// ReviewGate
// ─────────────────────────────────────────────────────────────
const ReviewGate = ({ execution, onApprove, onReject, onRegenerate, loading }) => {
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const handleReject = () => {
    if (rejectReason.trim()) {
      onReject(rejectReason);
      setShowRejectModal(false);
      setRejectReason('');
    }
  };

  if (execution.status === 'approved') {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-green-700">
          <CheckCircle2 className="w-5 h-5" />
          <span className="font-medium">Execution approvata</span>
        </div>
      </div>
    );
  }

  if (execution.status === 'rejected') {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-red-700">
          <XCircle className="w-5 h-5" />
          <span className="font-medium">Execution rigettata</span>
        </div>
        {execution.rejectionReason && (
          <p className="text-sm text-red-600 mt-2">{execution.rejectionReason}</p>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="font-medium text-gray-800 mb-3">Revisiona Output</h3>
        <div className="flex gap-3">
          <button onClick={() => onApprove()} disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50">
            <CheckCircle2 className="w-4 h-4" /> Approva
          </button>
          <button onClick={onRegenerate} disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50">
            <RefreshCw className="w-4 h-4" /> Rigenera
          </button>
          <button onClick={() => setShowRejectModal(true)} disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50">
            <XCircle className="w-4 h-4" /> Rigetta
          </button>
        </div>
      </div>

      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Motivo del Rigetto</h3>
            <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Spiega perche rigetti questa execution..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              rows={4} />
            <div className="flex gap-3 mt-4">
              <button onClick={() => setShowRejectModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                Annulla
              </button>
              <button onClick={handleReject} disabled={!rejectReason.trim()}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">
                Conferma Rigetto
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ─────────────────────────────────────────────────────────────
// FieldCard
// ─────────────────────────────────────────────────────────────
const FieldCard = ({ icon: Icon, label, value, colorClass, bgClass }) => {
  if (!value) return null;
  return (
    <div className={`rounded-lg border p-4 ${bgClass}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${colorClass}`} />
        <span className={`text-xs font-semibold uppercase tracking-wide ${colorClass}`}>{label}</span>
      </div>
      <p className="text-sm text-gray-700 leading-relaxed">{value}</p>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// StringList
// ─────────────────────────────────────────────────────────────
const StringList = ({ items }) => {
  if (!items || items.length === 0) return <p className="text-gray-400 text-sm italic">Nessun elemento</p>;
  return (
    <ul className="space-y-1">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2 text-sm">
          <span className="mt-1 w-1.5 h-1.5 rounded-full bg-gray-400 flex-shrink-0" />
          <span className="text-gray-700">{typeof item === 'string' ? item : JSON.stringify(item)}</span>
        </li>
      ))}
    </ul>
  );
};

// ─────────────────────────────────────────────────────────────
// AlternativesSection
// ─────────────────────────────────────────────────────────────
const AlternativesSection = ({ label, items = [] }) => {
  const [expanded, setExpanded] = useState(false);
  const validItems = items.filter(Boolean);
  if (!validItems.length) return null;
  return (
    <div className="rounded-lg border border-gray-200 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
          Alternatives: {label} ({validItems.length})
        </span>
        {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>
      {expanded && (
        <div className="p-3 space-y-2">
          {validItems.map((item, i) => (
            <div key={i} className="flex items-start gap-2">
              <span className="w-5 h-5 flex-shrink-0 flex items-center justify-center rounded-full bg-gray-100 text-xs text-gray-500">{i + 1}</span>
              <p className="text-sm text-gray-600">{typeof item === 'string' ? item : JSON.stringify(item)}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// EmptyState
// ─────────────────────────────────────────────────────────────
const EmptyState = () => (
  <div className="flex items-center justify-center h-full">
    <div className="text-center text-gray-500">
      <Clock className="w-12 h-12 mx-auto mb-4 text-gray-300" />
      <p className="text-lg font-medium mb-2">In attesa di esecuzione</p>
      <p className="text-sm">Clicca "Esegui Contract" per avviare il Policy Perception Engine</p>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────
// Contract04View
// ─────────────────────────────────────────────────────────────
const Contract04View = ({ execution, onApprove, onReject, onRegenerate, loading }) => {
  if (!execution) return <EmptyState />;

  const output = execution.output || execution.outputData || {};

  const hasHigherOrder = output.policy_under_detected_persuasion
    || output.policy_under_undetected_persuasion
    || output.higher_order_policy_robustness
    || output.epistemic_agency_preservation
    || output.freedom_of_refusal
    || output.persuasion_detection_efe_effect
    || (output.alternative_policy_availability?.length > 0);

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="max-w-4xl mx-auto space-y-6">

        {/* Header */}
        <div>
          <div className="flex items-center gap-3 mb-1">
            <Eye className="w-6 h-6 text-green-600" />
            <h2 className="text-xl font-bold text-gray-800">Policy Perception Engine</h2>
            <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full font-medium">C04</span>
          </div>
          <p className="text-sm text-gray-500">Come deve essere percepita la policy implicita della creativita</p>
        </div>

        {/* Primary Output — perception fields */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Output Primario</h3>

          <FieldCard
            icon={Eye}
            label="Policy Perception"
            value={output.policy_perception}
            colorClass="text-green-700"
            bgClass="bg-green-50 border-green-200"
          />

          <div className="grid grid-cols-2 gap-3">
            <FieldCard
              icon={AlertTriangle}
              label="Perceived Risk"
              value={output.perceived_risk}
              colorClass="text-red-700"
              bgClass="bg-red-50 border-red-200"
            />
            <FieldCard
              icon={Activity}
              label="Perceived Ambiguity"
              value={output.perceived_ambiguity}
              colorClass="text-orange-700"
              bgClass="bg-orange-50 border-orange-200"
            />
            <FieldCard
              icon={Users}
              label="Perceived Pressure"
              value={output.perceived_pressure}
              colorClass="text-purple-700"
              bgClass="bg-purple-50 border-purple-200"
            />
            <FieldCard
              icon={ArrowLeftRight}
              label="Perceived Reciprocity"
              value={output.perceived_reciprocity}
              colorClass="text-blue-700"
              bgClass="bg-blue-50 border-blue-200"
            />
          </div>

          <FieldCard
            icon={MousePointer}
            label="CTA Acceptability"
            value={output.cta_acceptability}
            colorClass="text-teal-700"
            bgClass="bg-teal-50 border-teal-200"
          />
        </div>

        {/* Policy Value Fields */}
        {(output.policy_pragmatic_value || output.policy_epistemic_value
          || output.policy_ambiguity_reduction || output.policy_verifiability) && (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Policy Value</h3>
            <div className="grid grid-cols-2 gap-3">
              <FieldCard
                icon={Lightbulb}
                label="Pragmatic Value"
                value={output.policy_pragmatic_value}
                colorClass="text-yellow-700"
                bgClass="bg-yellow-50 border-yellow-200"
              />
              <FieldCard
                icon={Search}
                label="Epistemic Value"
                value={output.policy_epistemic_value}
                colorClass="text-blue-700"
                bgClass="bg-blue-50 border-blue-200"
              />
            </div>
            <FieldCard
              icon={Activity}
              label="Ambiguity Reduction"
              value={output.policy_ambiguity_reduction}
              colorClass="text-orange-700"
              bgClass="bg-orange-50 border-orange-200"
            />
            <FieldCard
              icon={Shield}
              label="Policy Verifiability"
              value={output.policy_verifiability}
              colorClass="text-green-700"
              bgClass="bg-green-50 border-green-200"
            />
          </div>
        )}

        {/* Higher-Order Policy Evaluation */}
        {hasHigherOrder && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-violet-600" />
              <h3 className="text-sm font-semibold text-violet-700 uppercase tracking-wide">Higher-Order Policy Evaluation</h3>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <FieldCard
                icon={Eye}
                label="Policy — Detected Persuasion (B)"
                value={output.policy_under_detected_persuasion}
                colorClass="text-violet-700"
                bgClass="bg-violet-50 border-violet-200"
              />
              <FieldCard
                icon={Eye}
                label="Policy — Undetected Persuasion (A)"
                value={output.policy_under_undetected_persuasion}
                colorClass="text-indigo-700"
                bgClass="bg-indigo-50 border-indigo-200"
              />
            </div>

            <FieldCard
              icon={Shield}
              label="Higher-Order Policy Robustness"
              value={output.higher_order_policy_robustness}
              colorClass="text-violet-700"
              bgClass="bg-violet-50 border-violet-200"
            />

            <div className="grid grid-cols-2 gap-3">
              <FieldCard
                icon={Scale}
                label="Epistemic Agency Preservation"
                value={output.epistemic_agency_preservation}
                colorClass="text-teal-700"
                bgClass="bg-teal-50 border-teal-200"
              />
              <FieldCard
                icon={Scale}
                label="Freedom of Refusal"
                value={output.freedom_of_refusal}
                colorClass="text-teal-700"
                bgClass="bg-teal-50 border-teal-200"
              />
            </div>

            <FieldCard
              icon={Activity}
              label="Persuasion Detection EFE Effect"
              value={output.persuasion_detection_efe_effect}
              colorClass="text-indigo-700"
              bgClass="bg-indigo-50 border-indigo-200"
            />

            {output.alternative_policy_availability?.length > 0 && (
              <div className="rounded-lg border border-teal-200 bg-teal-50 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <List className="w-4 h-4 text-teal-600" />
                  <span className="text-xs font-semibold uppercase tracking-wide text-teal-700">Alternative Policy Availability</span>
                </div>
                <StringList items={output.alternative_policy_availability} />
              </div>
            )}
          </div>
        )}

        {/* Alternatives */}
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Configurazioni Alternative</h3>
          <AlternativesSection label="Policy Perception" items={output.alternative_policy_perception} />
          <AlternativesSection label="Perceived Risk" items={output.alternative_perceived_risk} />
          <AlternativesSection label="Perceived Ambiguity" items={output.alternative_perceived_ambiguity} />
          <AlternativesSection label="Perceived Pressure" items={output.alternative_perceived_pressure} />
          <AlternativesSection label="Perceived Reciprocity" items={output.alternative_perceived_reciprocity} />
          <AlternativesSection label="CTA Acceptability" items={output.alternative_cta_acceptability} />
          <AlternativesSection label="Pragmatic Value" items={output.alternative_policy_pragmatic_value} />
          <AlternativesSection label="Epistemic Value" items={output.alternative_policy_epistemic_value} />
          <AlternativesSection label="Ambiguity Reduction" items={output.alternative_policy_ambiguity_reduction} />
          <AlternativesSection label="Policy Verifiability" items={output.alternative_policy_verifiability} />
          <AlternativesSection label="Policy under Detected Persuasion" items={output.alternative_policy_under_detected_persuasion} />
          <AlternativesSection label="Higher-Order Policy Robustness" items={output.alternative_higher_order_policy_robustness} />
        </div>

        {/* Review Gate */}
        <ReviewGate
          execution={execution}
          onApprove={onApprove}
          onReject={onReject}
          onRegenerate={onRegenerate}
          loading={loading}
        />
      </div>
    </div>
  );
};

export default Contract04View;
